/**
 * WEB-1078 п.3 — фотография как фон экрана с отключённой вуалью (`overlay:
 * 'none'`) не защищала текст вовсе: цвет текста ничем не фиксировался, и
 * живой замер дал контраст 1.07-1.56 при норме 3:1 — текст поверх фото
 * фактически невидим.
 *
 * Решение владельца продукта (09.08): не выключать вуаль совсем, а всегда
 * накладывать тёмную вуаль РОВНО той силы, которая нужна для нормы — не
 * фиксированную «на глаз» непрозрачность (как у scrim-full/scrim-bottom), а
 * посчитанную от РЕАЛЬНОЙ яркости конкретной фотографии. Тёмное фото почти не
 * темнеет, светлое — заметно; фото остаётся видно, вид меняется минимально.
 *
 * Под разными частями текста фотография разной яркости — сила берётся по
 * НЕСКОЛЬКИМ точкам (сэмплирование живёт в useAdaptivePhotoScrim.ts, этот
 * модуль про него не знает). Слой вуали один на весь экран, поэтому итоговая
 * непрозрачность обязана хватать под САМОЙ яркой сэмплированной точкой —
 * отсюда максимум по точкам, а не среднее.
 *
 * Модуль чистый (без DOM/canvas) — только счёт, юнит-тестируется напрямую.
 */
import { contrastRatio } from './colorUtils';

/** Норма читаемости фото-фона — та же 3:1, которой замеряна карточка WEB-1078. */
export const ADAPTIVE_SCRIM_TARGET_CONTRAST = 3;

/**
 * Пока картинка не просэмплирована (загрузка/CORS-запрет на чтение
 * канваса/ошибка сети) — держим силу scrim-full: белый текст на ЛЮБОЙ, даже
 * полностью белой, фотографии при такой непрозрачности уже проходит норму
 * 3:1 (см. тест «фолбэк сильнее, чем требует даже полностью белое фото»).
 * Значит фолбэк не просто «не хуже прежнего», а безусловно достаточен —
 * деградация до него теряет только минимальность вуали, не читаемость.
 */
export const ADAPTIVE_SCRIM_FALLBACK_OPACITY = 0.55;

/** Текст, под который подбирается вуаль — светлый (ScreenBackgroundWrapper пиннит #ffffff на этом пути, как и на остальных сценариях со скримом). */
const TEXT_COLOR = '#ffffff';

export type SampleRgb = readonly [number, number, number];

function toHex([r, g, b]: SampleRgb): string {
  const c = (v: number) =>
    Math.round(Math.min(255, Math.max(0, v)))
      .toString(16)
      .padStart(2, '0');
  return `#${c(r)}${c(g)}${c(b)}`;
}

/**
 * Alpha-композит цвета поверх чёрного — так же, как браузер рисует
 * `rgba(0,0,0,opacity)` поверх фонового изображения (по закодированным
 * sRGB-каналам, без перехода в линейный свет).
 */
function blendWithBlack([r, g, b]: SampleRgb, opacity: number): SampleRgb {
  const keep = 1 - opacity;
  return [r * keep, g * keep, b * keep];
}

/**
 * Минимальная непрозрачность вуали для ОДНОЙ точки фото, при которой светлый
 * текст держит целевой контраст.
 *
 * Функция монотонна: больше вуали — темнее фон под ней — выше контраст с
 * белым текстом. Поэтому двоичный поиск сходится всегда, а предельный случай
 * (opacity=1 → чёрный фон, контраст 21:1) гарантированно проходит любую
 * разумную норму — подбор не может «не найти» решение.
 */
export function requiredScrimOpacityForSample(
  sample: SampleRgb,
  targetContrast: number = ADAPTIVE_SCRIM_TARGET_CONTRAST,
): number {
  const holds = (opacity: number): boolean =>
    contrastRatio(TEXT_COLOR, toHex(blendWithBlack(sample, opacity))) >= targetContrast;

  if (holds(0)) return 0;

  let lo = 0;
  let hi = 1;
  for (let i = 0; i < 24; i += 1) {
    const mid = (lo + hi) / 2;
    if (holds(mid)) {
      hi = mid;
    } else {
      lo = mid;
    }
  }
  return Math.round(hi * 100) / 100;
}

/**
 * Непрозрачность вуали по НЕСКОЛЬКИМ точкам фото (WEB-1078: «под разными
 * частями текста фотография разной яркости — считай по нескольким точкам, а
 * не по одной»). Берёт максимум — вуаль одна на весь слой, обязана хватать
 * под самой яркой из сэмплированных точек; точки темнее уже проходят норму с
 * запасом при таком же значении.
 *
 * Пустой список сэмплов (сэмплирование не удалось) — безопасный фолбэк, а не
 * ноль: ноль означал бы «вуаль не нужна», что при неизвестной яркости фото
 * неверно и вернуло бы старое незащищённое поведение.
 */
export function requiredScrimOpacityForSamples(
  samples: readonly SampleRgb[],
  targetContrast: number = ADAPTIVE_SCRIM_TARGET_CONTRAST,
): number {
  if (samples.length === 0) return ADAPTIVE_SCRIM_FALLBACK_OPACITY;
  return samples.reduce(
    (max, sample) => Math.max(max, requiredScrimOpacityForSample(sample, targetContrast)),
    0,
  );
}
